// ⚠️  FILE GENERATO AUTOMATICAMENTE — NON MODIFICARE A MANO.
// Fonte unica: shared/cargosRules.js (vedi lì per spiegazione e commenti).
// Rigenerato da scripts/sync-shared-rules.js ad ogni build/deploy.
// Se hai bisogno di cambiare questa logica, modifica SOLO shared/cargosRules.js.

/**
 * shared/cargosRules.js — FONTE UNICA delle regole del tracciato CaRGOS che
 * servono sia al server (functions/index.js: costruzione e invio del record) sia
 * al frontend (campi mancanti sulla scheda prenotazione, form cliente).
 *
 * Riferimenti: manuale tecnico CaRGOS "Servizio di invio dei contratti di
 * noleggio" Rev. 01 del 06/02/2024 (§5 Tracciato record: set di caratteri,
 * campi obbligatori e lunghezze minime) e https://cargos.poliziadistato.it/CARGOS_API/.
 *
 * CommonJS puro, niente spread: copiato in src/utils/ e functions/ da
 * scripts/sync-shared-rules.js. Modifica SOLO qui.
 */

// ── Set di caratteri del manuale ────────────────────────────────────────────
//  1 = lettere, accentate (à è ì ò ù), tedesche (ä ö ü ß), apostrofo, spazio
//  2 = set 1 + cifre + . ,
//  3 = lettere (a-z) + cifre + - . spazio
//  4 = lettere (a-z) + cifre + - . , ' spazio
//  5 = solo cifre
//  6 = lettere (a-z) + cifre + - . +   (niente spazio)
const GRAVE = 'ÀÈÌÒÙ';
const GERMAN = 'ÄÖÜ';
const EXTRA_LATIN = { 'Æ': 'AE', 'Œ': 'OE', 'Ø': 'O', 'Ł': 'L', 'Đ': 'D', 'Ð': 'D', 'Þ': 'TH', 'ẞ': 'SS' };

function allowedChar(ch, set) {
  const isBase = ch >= 'A' && ch <= 'Z';
  const isDigit = ch >= '0' && ch <= '9';
  switch (set) {
    case 1: return isBase || GRAVE.indexOf(ch) >= 0 || GERMAN.indexOf(ch) >= 0 || ch === "'" || ch === ' ';
    case 2: return isBase || GRAVE.indexOf(ch) >= 0 || GERMAN.indexOf(ch) >= 0 || isDigit
      || ch === '.' || ch === ',' || ch === "'" || ch === ' ';
    case 3: return isBase || isDigit || ch === '-' || ch === '.' || ch === ' ';
    case 4: return isBase || isDigit || ch === '-' || ch === '.' || ch === ',' || ch === "'" || ch === ' ';
    case 5: return isDigit;
    case 6: return isBase || isDigit || ch === '-' || ch === '.' || ch === '+';
    default: return false;
  }
}

/**
 * Porta un testo nel set di caratteri richiesto dal campo: maiuscolo, lettere
 * con diacritici non ammessi ridotte alla lettera base (é→E, ñ→N), apostrofi
 * tipografici normalizzati, tutto il resto (/ - ( ) ecc. se non ammesso)
 * sostituito da spazio; spazi multipli compattati. Set 5 = solo cifre.
 * opts.keepCase: non cambia maiuscole/minuscole (per identificativi come
 * CONTRATTO_ID o AGENZIA_ID dove la differenza conta).
 */
function sanitizeCargosText(value, set, maxLen, opts) {
  const keepCase = Boolean(opts && opts.keepCase);
  let src = value === null || value === undefined ? '' : String(value);
  src = src.replace(/[\u2018\u2019\u02BC`\u00B4]/g, "'");
  if (!keepCase) src = src.toUpperCase();
  let out = '';
  for (const raw of src) {
    const up = keepCase ? raw.toUpperCase() : raw;
    if (allowedChar(up, set)) { out += raw; continue; }
    if (EXTRA_LATIN[up]) { out += keepCase && raw !== up ? EXTRA_LATIN[up].toLowerCase() : EXTRA_LATIN[up]; continue; }
    const base = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (base !== raw && base.length > 0 && Array.prototype.every.call(base, (c) => allowedChar(c.toUpperCase(), set))) {
      out += base;
      continue;
    }
    if (set !== 5) out += ' ';
  }
  if (set === 6 || set === 5) out = out.replace(/ /g, '');
  else out = out.replace(/ {2,}/g, ' ').trim();
  return typeof maxLen === 'number' ? out.slice(0, maxLen).trim() : out;
}

/** Codice luogo/stato valido: 9 cifre, non tutte zero. */
function isCargosPlaceCode(v) {
  const s = v === null || v === undefined ? '' : String(v).trim();
  return /^\d{9}$/.test(s) && Number(s) > 0;
}

/** "via, cap città" saltando le parti vuote o già contenute nella via. */
function composeAddress(street, cap, citta) {
  const clean = (v) => (v === null || v === undefined ? '' : String(v).trim());
  const via = clean(street);
  const rest = [clean(cap), clean(citta)].filter((part) => part && !via.toLowerCase().includes(part.toLowerCase()));
  return [via, rest.join(' ')].filter(Boolean).join(', ');
}

/** Indirizzo di residenza di un contatto (campo esplicito oppure via/cap/città). */
function contactResidenceAddress(contact) {
  const c = contact || {};
  return String(c.indirizzo || c.residenzaIndirizzo || composeAddress(c.via, c.cap, c.citta) || '').trim();
}

/**
 * Dati del CONTRAENTE obbligatori per CaRGOS ("Obbl = SI" nel manuale) che
 * mancano. Ritorna un array di etichette leggibili (vuoto se completo).
 *  - numero documento e numero patente: lunghezza > 4 dopo la pulizia (set 3)
 *  - luoghi: devono essere codici della Tabella Luoghi (9 cifre, non zero)
 *  - residenza (facoltativa): se c'è il codice del comune serve anche l'indirizzo
 */
/**
 * Data di nascita utilizzabile nel record: giorno reale del calendario, dal 1900 e non nel futuro. Formati: aaaa-mm-gg (come la
 * salvano i form; un eventuale orario dopo la «T» si ignora, come fa il costruttore del record) e gg/mm/aaaa (il costruttore lo
 * lascia passare invariato). Stessa regola di `isValidBirthDate` in src/utils/birthInfo.js, che però accetta solo l'ISO.
 */
function isPlausibleBirthDate(value) {
  const s = String(value == null ? '' : value).trim();
  let y; let mo; let d;
  let m = /^(\d{4})-(\d{2})-(\d{2})(?:$|T)/.exec(s);
  if (m) { y = Number(m[1]); mo = Number(m[2]); d = Number(m[3]); }
  else {
    m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
    if (!m) return false;
    d = Number(m[1]); mo = Number(m[2]); y = Number(m[3]);
  }
  const dt = new Date(Date.UTC(y, mo - 1, d));
  if (dt.getUTCFullYear() !== y || dt.getUTCMonth() !== mo - 1 || dt.getUTCDate() !== d) return false;
  return y >= 1900 && dt.getTime() <= Date.now();
}

/**
 * `opts.skipLookups`: non pretende i dati che il cliente sceglie cercando in un elenco (luoghi e tipo di documento) quando il servizio di
 * ricerca non è disponibile: in quel caso un guasto non deve impedire la firma, e li completa l'azienda. Uso: solo nella firma del cliente.
 */
function cargosContactMissing(contact, opts) {
  const c = contact || {};
  const skipLookups = Boolean(opts && opts.skipLookups);
  const out = [];
  const has = (v) => typeof v === 'string' && v.trim().length > 0;
  // Cognome e nome si controllano DOPO la sanificazione (come numero documento e patente): un nome in alfabeto cirillico, cinese,
  // arabo… o fatto di soli simboli diventerebbe vuoto nel record e il portale lo rifiuterebbe come campo obbligatorio mancante.
  if (!has(c.cognome)) out.push('Cognome');
  else if (!sanitizeCargosText(c.cognome, 1).length) out.push('Cognome (scrivilo in lettere latine, come sul documento)');
  if (!has(c.nome)) out.push('Nome');
  else if (!sanitizeCargosText(c.nome, 1).length) out.push('Nome (scrivilo in lettere latine, come sul documento)');
  if (!has(c.dataNascita)) out.push('Data di nascita');
  else if (!isPlausibleBirthDate(c.dataNascita)) out.push('Data di nascita (non valida)');
  if (!skipLookups && !isCargosPlaceCode(c.cargosNascitaLuogoCod)) out.push('Luogo di nascita');
  if (!skipLookups && !isCargosPlaceCode(c.cargosCittadinanzaCod)) out.push('Cittadinanza');
  if (!skipLookups && !has(c.cargosDocTipoCod)) out.push('Tipo di documento d\'identità');
  if (sanitizeCargosText(c.numeroDocumento, 3).length < 5) out.push('Numero del documento d\'identità (min. 5 caratteri)');
  if (!skipLookups && !isCargosPlaceCode(c.cargosDocRilascioLuogoCod)) out.push('Luogo di rilascio del documento');
  if (sanitizeCargosText(c.numeroPatente, 3).length < 5) out.push('Numero della patente (min. 5 caratteri)');
  if (!skipLookups && !isCargosPlaceCode(c.cargosPatenteRilascioLuogoCod)) out.push('Luogo di rilascio della patente');
  if (isCargosPlaceCode(c.cargosResidenzaLuogoCod) && sanitizeCargosText(contactResidenceAddress(c), 2).length < 4) {
    out.push('Indirizzo di residenza (obbligatorio se indichi il comune di residenza)');
  }
  return out;
}

/**
 * Tipi di veicolo dell'app che CaRGOS NON riguarda: il D.M. 29/10/2021 definisce «autoveicoli» i veicoli a
 * motore con almeno quattro ruote, esclusi i motoveicoli (art. 54 D.Lgs. 285/1992); nella tabella
 * TIPO_VEICOLO del portale non esiste nemmeno una voce per le moto.
 */
const CARGOS_MOTOVEICOLI = ['moto', 'scooter', 'motorino', 'ciclomotore', 'motociclo', 'motoveicolo'];

// ── Modalità di pagamento in cassa → TIPO_PAGAMENTO del portale ─────────────
// Il portale distingue Contanti / Carta di Credito / Carta di Debito / Bonifico. Per le prenotazioni inserite
// a mano (metodo_pagamento = 'cassa_manuale') la modalità reale sta in `cassaMetodo`; senza, resta "contanti".
const CASSA_METODI = [
  { value: 'contanti',      label: 'Contanti' },
  { value: 'carta_credito', label: 'Carta di credito (POS)' },
  { value: 'carta_debito',  label: 'Carta di debito / Bancomat (POS)' },
  { value: 'bonifico',      label: 'Bonifico' },
];
const CASSA_METODO_KIND = { contanti: 'cash', carta_credito: 'credit', carta_debito: 'debit', bonifico: 'transfer' };

function foldPaymentText(v) {
  return String(v == null ? '' : v).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();
}

/** 'cash' | 'credit' | 'debit' | 'transfer' | null (non determinabile). Accetta la prenotazione o la sola stringa. */
function cargosPaymentKind(bookingOrMetodo) {
  const isObj = bookingOrMetodo && typeof bookingOrMetodo === 'object';
  const m = foldPaymentText(isObj ? bookingOrMetodo.metodo_pagamento : bookingOrMetodo);
  if (!m) return null;
  if (m === 'stripe') return 'credit';
  // "bonifico_manuale" va controllato PRIMA di "manuale": prima finiva tra i contanti.
  if (/bonific/.test(m)) return 'transfer';
  if (/debit|bancomat/.test(m)) return 'debit';
  if (/carta|card|credit/.test(m)) return 'credit';
  if (/cassa|contant|cash|manual/.test(m)) {
    const sub = CASSA_METODO_KIND[foldPaymentText(isObj ? bookingOrMetodo.cassaMetodo : '')];
    return sub || 'cash';
  }
  return null;
}

/** Etichetta leggibile della modalità di pagamento (fatture, contratto, schede). */
function paymentMethodLabel(booking) {
  const b = booking || {};
  const m = foldPaymentText(b.metodo_pagamento);
  if (!m) return '';
  if (m === 'stripe') return 'Carta di credito (online)';
  const kind = cargosPaymentKind(b);
  if (kind === 'transfer') return 'Bonifico';
  if (kind === 'debit') return 'Carta di debito (POS)';
  if (kind === 'credit') return 'Carta di credito (POS)';
  if (kind === 'cash') return /cassa/.test(m) ? 'Contanti / Cassa' : 'Contanti';
  return String(b.metodo_pagamento);
}

// ── Noleggi oltre 30 giorni: annotazione nell'Archivio nazionale dei veicoli ─
// Art. 94 c. 4-bis CdS: locazione senza conducente per più di 30 giorni → annotazione entro 30 giorni.
// ⚠️ Chi debba presentare la pratica (locatario o noleggiatore) e da quando decorrano i 30 giorni non è
// confermato da una fonte ufficiale letta: l'app NON presenta nulla, tiene solo un promemoria. Scelta
// prudenziale: il termine parte dalla data di inizio del noleggio.
const ANNOTAZIONE_GIORNI = 30;
// Annullata o già riconsegnata: il promemoria non serve più.
const STATI_NON_ATTIVI = ['prenotazione_annullata', 'annullata', 'auto_riconsegnata'];

function isoDayNumber(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return NaN;
  return Math.round(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])) / 86400000);
}
function dayNumberToIso(n) {
  return new Date(n * 86400000).toISOString().slice(0, 10);
}

/** Durata in giorni tra dataInizio e dataFine (stesso calcolo usato per l'esenzione CaRGOS). NaN se mancano le date. */
function bookingDurationDays(booking) {
  const a = isoDayNumber(booking && booking.dataInizio);
  const b = isoDayNumber(booking && booking.dataFine);
  return b - a;
}

/**
 * Stato dell'annotazione per un noleggio > 30 giorni, oppure null se non c'entra.
 * stato: 'fatta' | 'scaduta' | 'in_scadenza' (≤ 7 giorni) | 'da_fare'.
 * Registrata dall'admin in booking.annotazioneArchivio = { fatta: true, data, riferimento, da }.
 */
function archiveAnnotationInfo(booking, todayIso) {
  if (!booking || STATI_NON_ATTIVI.indexOf(booking.stato) >= 0) return null;
  const durata = bookingDurationDays(booking);
  if (!(durata > 30)) return null;
  const start = isoDayNumber(booking.dataInizio);
  const scadenza = dayNumberToIso(start + ANNOTAZIONE_GIORNI);
  const rec = booking.annotazioneArchivio;
  if (rec && rec.fatta === true) {
    return { stato: 'fatta', scadenza, giorniRimasti: null, data: rec.data || '', riferimento: rec.riferimento || '', durata };
  }
  const oggi = isoDayNumber(todayIso);
  const giorniRimasti = Number.isFinite(oggi) ? start + ANNOTAZIONE_GIORNI - oggi : null;
  let stato = 'da_fare';
  if (giorniRimasti !== null) stato = giorniRimasti < 0 ? 'scaduta' : (giorniRimasti <= 7 ? 'in_scadenza' : 'da_fare');
  return { stato, scadenza, giorniRimasti, data: '', riferimento: '', durata };
}

/**
 * Il cliente deve fornire i dati per CaRGOS PRIMA di firmare? Sì solo se: l'azienda ha attivato CaRGOS (`cargosSoggetta === true`, che il
 * server imposta quando si salvano le credenziali; il default è false, quindi le aziende che non lo usano non cambiano), il noleggio dura
 * al massimo 30 giorni e il mezzo è un autoveicolo (non moto/scooter né bici, monopattini…). Stessi criteri con cui il server esenta l'invio.
 * La usano sia la pagina di firma sia la function `firmaContrattoCliente`: devono decidere allo stesso modo.
 */
function cargosCustomerDataRequired(booking, car, azienda) {
  if (!azienda || azienda.cargosSoggetta !== true) return false;
  if (bookingDurationDays(booking) > 30) return false;
  const tipo = String((car && car.tipoVeicolo) || (booking && booking.tipoVeicolo) || '').toLowerCase();
  if (CARGOS_MOTOVEICOLI.indexOf(tipo) !== -1) return false;
  if (['bici', 'bicicletta', 'monopattino', 'ebike'].indexOf(tipo) !== -1) return false;
  return true;
}

module.exports = {
  CARGOS_MOTOVEICOLI,
  cargosCustomerDataRequired,
  sanitizeCargosText,
  isCargosPlaceCode,
  composeAddress,
  contactResidenceAddress,
  cargosContactMissing,
  CASSA_METODI,
  cargosPaymentKind,
  paymentMethodLabel,
  ANNOTAZIONE_GIORNI,
  bookingDurationDays,
  archiveAnnotationInfo,
};
